// Deterministic, server-ready WEATHER. A seeded schedule blends between preset states; the renderers just read
// the interpolated `state` (cloud cover, light, fog, cloud darkness -- rain + lightning hook in as later phases).
// The schedule is a hash of (seed + period index), so it's reproducible and multiplayer-consistent for free; the
// server can later drive it by feeding the same seed or by writing `state` directly. The visuals don't care
// where the numbers come from.

export interface WeatherSnapshot {
  light: number;      // sunlight multiplier (1 = full sun, < 1 = overcast/dim)
  fog: number;        // extra fog density (0 = clear, pulls the fog far plane in)
  cloudDark: number;  // cloud colour multiplier (1 = bright white, < 1 = grey storm clouds)
  cloudCover: number; // bias added to the cloud-density field (< 0 = clearer sky, > 0 = more clouds + shadows)
  rain: number;       // 0..1 rain intensity (Phase 3)
}
interface Preset extends WeatherSnapshot { weight: number; name: string; }

// Phase 2 presets (no rain yet). Weighted: mostly sunny/fair, overcast is the rare gloomy day.
const PRESETS: Preset[] = [
  { name: 'sunny',    weight: 5, light: 1.00, fog: 0.00, cloudDark: 1.00, cloudCover: -0.16, rain: 0 },
  { name: 'fair',     weight: 3, light: 0.93, fog: 0.05, cloudDark: 0.94, cloudCover: -0.04, rain: 0 },
  { name: 'cloudy',   weight: 2, light: 0.78, fog: 0.12, cloudDark: 0.78, cloudCover:  0.08, rain: 0 },
  { name: 'overcast', weight: 1, light: 0.52, fog: 0.30, cloudDark: 0.50, cloudCover:  0.24, rain: 0 },
];
const TOTAL_W = PRESETS.reduce((s, p) => s + p.weight, 0);
const PERIOD = 0.4; // in-game days per weather period (a couple of changes per day)

function hash01(n: number): number {
  let h = (n >>> 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export class WeatherSystem {
  readonly state: WeatherSnapshot = { light: 1, fog: 0, cloudDark: 1, cloudCover: 0, rain: 0 };
  private seed: number;
  constructor(seed = 20260626) { this.seed = seed >>> 0; }

  // MP join: adopt the world's shared seed so every client rolls the identical schedule.
  reseed(seed: number): void { this.seed = seed >>> 0; }

  // The weather for a given period index: a weighted, deterministic roll.
  private presetFor(period: number): Preset {
    let r = hash01(this.seed + period * 0x9e3779b1) * TOTAL_W;
    for (const p of PRESETS) { if (r < p.weight) return p; r -= p.weight; }
    return PRESETS[0];
  }

  // Advance to the given in-game day count; crossfades between consecutive periods so weather drifts in smoothly.
  update(totalDays: number): void {
    const x = totalDays / PERIOD, k = Math.floor(x), f = x - k;
    const cur = this.presetFor(k), prev = this.presetFor(k - 1);
    const b = Math.min(1, f / 0.3); // crossfade over the first 30% of the period, then hold
    const s = this.state;
    s.light = prev.light + (cur.light - prev.light) * b;
    s.fog = prev.fog + (cur.fog - prev.fog) * b;
    s.cloudDark = prev.cloudDark + (cur.cloudDark - prev.cloudDark) * b;
    s.cloudCover = prev.cloudCover + (cur.cloudCover - prev.cloudCover) * b;
    s.rain = prev.rain + (cur.rain - prev.rain) * b;
  }
}
